<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, Text, Checkbox, ListItem, Stack, Token } from '@Shelly/shelly-design-system';
	import { devices, devices_settings } from 'src/stores';
	import { isCamera, parseAlarmDeviceRr, real_id } from 'src/helpers/interface_helpers';
	import { getSettings } from 'src/helpers/get_status_settings_helpers';
	import { getCameraMotionZones } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';
	import {
		buildCameraSceneToken,
		extractZoneIdFromCameraZoneCondition,
		isCameraZoneSceneCommand,
		CAMERA_COND_NAMES,
	} from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
	import { hex_to_decimal } from 'src/helpers/utilities';
	import type { IAlarmZone } from '../alarm_object';

	export let alarm_object: IAlarmZone;
	/** Selected Shelly camera devices from the Devices step (merged device records). */
	export let selectedCameraSensors: any[] = [];

	type CameraMode = 'all' | 'zones';

	const CAMERA_CONDITION_VALUE = 'true';
	const emptyCheckedSet = new Set<string>();

	const sensorIdToDecimal = (sensorId: string): string => (sensorId ? hex_to_decimal(sensorId) : '');

	$: hasAnyCamera = selectedCameraSensors.length > 0;

	$: selectedCameraDecimals = selectedCameraSensors
		.map((s) => sensorIdToDecimal(s?.id))
		.filter(Boolean);

	$: for (const sensor of selectedCameraSensors) {
		const sid = sensor?.id;
		if (sid && isCamera($devices[sid]?.type) && !$devices_settings[real_id(sid)]) {
			getSettings($devices[sid] ?? sensor);
		}
	}

	let modeByDevice: Record<string, CameraMode> = {};
	let checkedZonesByDevice: Record<string, Set<string>> = {};
	let cameraDecimalsInitKey = '';

	/** Read the whole-frame vs zone selection for a camera from the alarm script. */
	const readCameraStateFromAlarm = (decimal: string): { mode: CameraMode; zones: Set<string> } => {
		const zones = new Set<string>();
		for (const entry of alarm_object?.if?.or ?? []) {
			const parsed = parseAlarmDeviceRr(entry?.eq?.[0]?.rr);
			if (!parsed || parsed.decimal !== decimal) continue;
			if (isCameraZoneSceneCommand(parsed.component ?? '')) {
				const zoneId = extractZoneIdFromCameraZoneCondition(parsed.component);
				if (zoneId !== null) zones.add(zoneId);
			}
		}
		// Any zone token → zone mode; otherwise (whole-frame or nothing yet) → entire-screen.
		return { mode: zones.size > 0 ? 'zones' : 'all', zones };
	};

	/**
	 * Motion zones per camera, keyed by decimal. Derived reactively from the selected
	 * cameras and the settings store so the picker updates once settings finish loading.
	 */
	$: zonesByDevice = selectedCameraSensors.reduce<Record<string, { id: string; title: string }[]>>(
		(map, sensor) => {
			const sid = sensor?.id ?? '';
			if (sid && isCamera($devices[sid]?.type)) {
				map[sensorIdToDecimal(sid)] = getCameraMotionZones(sid, $devices_settings).map((z) => ({
					id: z.id,
					title: z.title,
				}));
			}
			return map;
		},
		{},
	);

	/**
	 * Strip every camera token for the selected cameras and rewrite them from the
	 * current mode/zone selection. XOR: a camera is either whole-frame (cam_motion1)
	 * or a set of zone tokens (camzone_motion{id+1}), never both. Conditions for other
	 * sensors are preserved.
	 */
	const syncAllCamerasToAlarmObject = () => {
		const cameraDecimals = new Set(selectedCameraDecimals);
		if (cameraDecimals.size === 0) return;

		const withoutSelectedCameras = (alarm_object.if.or ?? []).filter((e) => {
			const parts = e?.eq?.[0]?.rr?.split(':') ?? [];
			if (parts[0] !== 'dev') return true;
			return !cameraDecimals.has(parts[1]);
		});

		const additions: Array<{ eq: [{ rr: string }, string] }> = [];
		for (const dec of cameraDecimals) {
			const mode = modeByDevice[dec] ?? 'all';
			if (mode === 'zones') {
				const checked = checkedZonesByDevice[dec] ?? new Set();
				for (const zoneId of checked) {
					const token = buildCameraSceneToken(CAMERA_COND_NAMES.ZONE_MOTION, zoneId);
					if (token) {
						additions.push({ eq: [{ rr: `dev:${dec}:${token}` }, CAMERA_CONDITION_VALUE] });
					}
				}
			} else {
				const token = buildCameraSceneToken(CAMERA_COND_NAMES.MOTION, 0);
				if (token) {
					additions.push({ eq: [{ rr: `dev:${dec}:${token}` }, CAMERA_CONDITION_VALUE] });
				}
			}
		}

		alarm_object.if.or = [...withoutSelectedCameras, ...additions];
		alarm_object = alarm_object;
	};

	/** When the set of selected cameras changes, re-read state from the alarm. */
	$: {
		const key = selectedCameraDecimals.slice().sort().join('|');
		if (key !== cameraDecimalsInitKey) {
			cameraDecimalsInitKey = key;
			const nextMode: Record<string, CameraMode> = {};
			const nextChecked: Record<string, Set<string>> = {};
			for (const dec of selectedCameraDecimals) {
				const { mode, zones } = readCameraStateFromAlarm(dec);
				// No tokens + already in zone mode = user unchecked every zone.
				// Keep that invalid state so Next stays disabled (do not flip to entire screen).
				if (mode === 'all' && zones.size === 0 && modeByDevice[dec] === 'zones') {
					nextMode[dec] = 'zones';
					nextChecked[dec] = new Set();
				} else {
					nextMode[dec] = mode;
					nextChecked[dec] = zones;
				}
			}
			modeByDevice = nextMode;
			checkedZonesByDevice = nextChecked;
			syncAllCamerasToAlarmObject();
		}
	}

	const setMode = (decimal: string, mode: CameraMode) => {
		if ((modeByDevice[decimal] ?? 'all') === mode) return;
		const zones = zonesByDevice[decimal] ?? [];
		// Guard: cannot enter zone mode when the camera has no motion zones.
		if (mode === 'zones' && zones.length === 0) return;
		modeByDevice = { ...modeByDevice, [decimal]: mode };
		// Default: pre-select the first zone (usually Main). The user can uncheck
		// every zone; Next stays disabled until at least one is selected again.
		if (mode === 'zones' && (checkedZonesByDevice[decimal]?.size ?? 0) === 0) {
			checkedZonesByDevice = { ...checkedZonesByDevice, [decimal]: new Set([zones[0].id]) };
		}
		syncAllCamerasToAlarmObject();
	};

	const toggleZone = (decimal: string, zoneId: string) => {
		const prev = checkedZonesByDevice[decimal] ?? new Set<string>();
		const next = new Set(prev);
		if (next.has(zoneId)) {
			next.delete(zoneId);
		} else {
			next.add(zoneId);
		}
		checkedZonesByDevice = { ...checkedZonesByDevice, [decimal]: next };
		syncAllCamerasToAlarmObject();
	};

	const listItemAvatar = (sensor: any) =>
		sensor?.image || sensor?.icon || sensor?.backgroundColor || 'fak fa-solid-shelly';
</script>

<Container padding="scrollPaddings">
	{#if !hasAnyCamera}
		<Text styleType="body" class="text-muted">
			{$format('alarm_camera_no_camera_selected', {
				default:
					'No camera selected. Select one in the previous step to configure motion detection.',
			})}
		</Text>
	{:else}
		<Text element="h2" class="mb-s">
			{$format('alarm_camera_settings_heading', { default: 'Camera motion detection' })}
		</Text>
		<Text styleType="body-small" class="mb-m">
			{$format('alarm_camera_settings_hint', {
				default:
					'Choose whether the whole camera view or specific motion zones should trigger this alarm.',
			})}
		</Text>

		<Stack gap="m">
			{#each selectedCameraSensors as sensor (sensor.id)}
				{@const sensorId = sensor?.id ?? ''}
				{@const decimal = sensorIdToDecimal(sensorId)}
				{@const zones = zonesByDevice[decimal] ?? []}
				{@const mode = modeByDevice[decimal] ?? 'all'}
				{@const checked = checkedZonesByDevice[decimal] ?? emptyCheckedSet}

				<div class="camera-settings-sensor-block">
					<ListItem
						class="mb-s"
						icon={listItemAvatar(sensor)}
						image={sensor?.image}
						title={sensor?.name ??
							$format('title_unknown_device', { default: 'Unknown Device' })}
						progress={{ percent: 0, showPercentBar: false }}
					/>

					<div class="d-flex flex-wrap mb-s" style="gap: 8px;">
						<Token
							id="camera-mode-all-{decimal}"
							size="m"
							title={$format('alarm_camera_entire_screen', { default: 'Entire screen' })}
							selected={mode === 'all'}
							on:click={() => setMode(decimal, 'all')}
						/>
						<Token
							id="camera-mode-zones-{decimal}"
							size="m"
							title={$format('camera_motion_zones', { default: 'Motion zones' })}
							selected={mode === 'zones'}
							disabled={zones.length === 0}
							on:click={() => setMode(decimal, 'zones')}
						/>
					</div>

					{#if mode === 'zones'}
						{#if zones.length === 0}
							<Text styleType="body-small" class="mb-0">
								{$format('alarm_camera_no_motion_zones', {
									default:
										'This camera has no motion zones. The whole camera view will be monitored.',
								})}
							</Text>
						{:else}
							{#each zones as zone (zone.id)}
								<Checkbox
									id="camera-zone-checkbox-{decimal}-{zone.id}"
									class="mb-s"
									size="m"
									checked={checked.has(zone.id)}
									on:click={() => toggleZone(decimal, zone.id)}
								>
									{zone.title}
								</Checkbox>
							{/each}
							{#if checked.size === 0}
								<Text styleType="body-small" class="mb-0">
									{$format('alarm_camera_select_at_least_one_zone', {
										default: 'Select at least one motion zone to continue.',
									})}
								</Text>
							{/if}
						{/if}
					{/if}
				</div>
			{/each}
		</Stack>
	{/if}
</Container>
